import { useEffect, useState } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { apiClient } from '../../lib/api-client';

type EnvironmentStatus = 'ready' | 'missing' | 'stale' | 'error';

interface CloudAgentEnvironmentRow {
  streamId: string;
  roleCode: string;
  roleTitle: string;
  repository: string;
  toolIds: string[];
  status: EnvironmentStatus;
  error?: string;
}

interface CloudAgentEnvironmentsDialogProps {
  open: boolean;
  onClose: () => void;
}

function statusLabel(status: EnvironmentStatus): string {
  if (status === 'ready') return 'Ready';
  if (status === 'stale') return 'Out of date';
  if (status === 'error') return 'Error';
  return 'Not set up';
}

function statusVariant(status: EnvironmentStatus): 'success' | 'warning' | 'outline' {
  if (status === 'ready') return 'success';
  if (status === 'error' || status === 'stale') return 'warning';
  return 'outline';
}

export function CloudAgentEnvironmentsDialog({ open, onClose }: CloudAgentEnvironmentsDialogProps) {
  const [rows, setRows] = useState<CloudAgentEnvironmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [setupRole, setSetupRole] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    apiClient.listCloudAgentEnvironments()
      .then((response) => {
        if (!cancelled) setRows(Array.isArray(response.environments) ? response.environments : []);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load cloud agent environments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const setup = async (targets: Array<{ streamId: string; roleCode: string }>, key: string) => {
    setSetupRole(key);
    setError('');
    try {
      const response = await apiClient.setupCloudAgentEnvironments(targets);
      setRows(Array.isArray(response.environments) ? response.environments : rows);
      const failed = (response.results || []).filter((result) => result.error);
      if (failed.length > 0) {
        setError(failed.map((result) => `${result.roleCode}: ${result.error}`).join(' '));
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Cloud agent environment setup failed');
    } finally {
      setSetupRole('');
    }
  };

  const missing = rows.filter((row) => row.status !== 'ready');

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent maxWidth="3xl">
        <DialogHeader>
          <DialogTitle>Cloud agent environments</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
            Each role repository gets `.cursor/environment.json` and an install script for that role&apos;s tools.
            Cursor builds the machine from the script on the default branch, so later runs start with the tools already installed.
            Syncing role artifacts writes the same files.
          </p>
          {error ? <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading environments...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No roles in this workspace.</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-gray-200 dark:border-gray-800">
              {rows.map((row) => {
                const key = `${row.streamId}/${row.roleCode}`;
                return (
                  <div key={key} className="flex items-start justify-between gap-3 border-b border-gray-200 px-3 py-2 last:border-b-0 dark:border-gray-800">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {row.roleTitle || row.roleCode}
                      </div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {row.streamId} / {row.repository || 'no repository'}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {row.toolIds.length > 0 ? row.toolIds.join(', ') : 'base tools only'}
                      </div>
                      {row.error ? <div className="mt-1 text-xs text-red-600 dark:text-red-400">{row.error}</div> : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant={statusVariant(row.status)}>{statusLabel(row.status)}</Badge>
                      {row.status !== 'ready' && row.repository ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={Boolean(setupRole)}
                          onClick={() => void setup([{ streamId: row.streamId, roleCode: row.roleCode }], key)}
                        >
                          {setupRole === key ? 'Setting up...' : 'Set up'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          <Button
            type="button"
            disabled={loading || Boolean(setupRole) || missing.length === 0}
            onClick={() => void setup(
              missing.filter((row) => row.repository).map((row) => ({ streamId: row.streamId, roleCode: row.roleCode })),
              '*',
            )}
          >
            {setupRole === '*' ? 'Setting up...' : 'Set up missing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
